import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { UPLOAD_FILE_SIZE_MB, UPLOAD_FILE_TYPE } from "../../../constants/app";
import { FILE_UPLOAD, PageRoutes } from "../../../constants/routes";
import { appActions } from "../../../ducks/app";
import {
  FileUploadResult,
  FileUploadTemplate,
  fileActions,
  fileSelectors,
} from "../../../ducks/file";
import { useNavigation } from "../../../hooks/useNavigation";
import {
  TableHeaderItem,
  TablePagination,
  TableSortBy,
} from "../../../hooks/useTable";
import { AppDispatch } from "../../../store";
import { formatDateTime } from "../../../utils/date";
import { SelectOptionType } from "../../atoms/Select";
import { FileStatus } from "../../molecules/file_upload/FileStatus";
import { getFileSizeInMB } from "../../../utils/file";
import { ErrorMessageManager } from "../../../core/http/ErrorMessageManager";
import { FileTypeNumber } from "../../../core/api/requests/file/download/types";

export const useFileUpload = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { navigateToPage } = useNavigation();
  const { menuId } = useParams<{ menuId: string }>();

  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<FileUploadTemplate | null>(null);
  const [currentSort, setCurrentSort] = useState<TableSortBy>({
    field: "file_name",
    order: "asc",
  });
  const [currentPagination, setCurrentPagination] = useState<TablePagination>({
    page: 1,
    size: 100,
  });

  const templates = useSelector(fileSelectors.templates);

  const results = useSelector(fileSelectors.results);

  const [rowResults, setRowResults] = useState<FileUploadResult[]>(
    results.data,
  );

  useEffect(() => {
    if (menuId) {
      dispatch(
        fileActions.getFileUploadTemplates.started({ menu_id: Number(menuId) }),
      );
    }
  }, []);

  useEffect(() => {
    setRowResults(results.data);
  }, [results]);

  useEffect(() => {
    if (templates.length === 1) {
      setSelectedTemplate(templates[0]);
    }
  }, [templates]);

  const pageTitle = useMemo(() => {
    if (!menuId) return "";

    switch (menuId) {
      case FILE_UPLOAD.COMMON_HUMAN_RESOURCE:
        return "人事ファイル（共通）";
      case FILE_UPLOAD.COMMON_RESERVATION:
        return "予約ファイル（共通）";
      case FILE_UPLOAD.INDIVIDUAL_HUMAN_RESOURCE:
        return "人事ファイル（個別）";
      default:
        return "";
    }
  }, [menuId]);

  const total = useMemo(() => results.total, [results]);

  const tableHeaders: TableHeaderItem<FileUploadResult>[] = useMemo(() => {
    return [
      {
        id: 1,
        title: "ファイル名",
        dataKey: "fileName",
        sortKey: "file_name",
        width: "30%",
      },
      { id: 2, title: "サイズ", dataKey: "fileSize", sortKey: "file_size" },
      {
        id: 3,
        title: "テンプレート",
        dataKey: "templateName",
        sortKey: "templates.template_name",
        width: "30%",
      },
      {
        id: 4,
        title: "実施日時",
        dataKey: "uploadDate",
        dataMapFunction: ({ uploadDate }) =>
          formatDateTime(uploadDate, false, "YYYY/MM/DD HH:mm"),
        sortKey: "upload_datetime",
      },
      {
        id: 5,
        title: "登録結果",
        dataKey: "result",
        dataMapFunction: ({ result }) => ({ status: result }),
        customComponent: FileStatus,
        sortKey: "result",
      },
    ];
  }, []);

  const templateOptions: SelectOptionType[] = useMemo(() => {
    return templates.map((template) => ({
      id: template.id,
      name: template.name,
      value: template.id,
    }));
  }, [templates]);

  const onError = useCallback(() => {
    setHasError(true);
    setFile(null);
    dispatch(
      appActions.showNotification({
        type: "failed",
        message: "ファイルのアップロードに失敗しました",
      }),
    );
  }, []);

  const handleFileUpload = useCallback(() => {
    if (!file || !selectedTemplate) return;

    setHasError(false);
    setIsUploading(true);
    dispatch(appActions.showLoading());

    dispatch(
      fileActions.uploadFile.started({
        file,
        menu_id: Number(menuId),
        template_id: selectedTemplate.id,
        onComplete: () => {
          dispatch(appActions.hideLoading());
          setIsUploading(false);
          fetchFileUploadResult(currentSort, currentPagination);
        },
        onError,
        onSuccess: (data) => {
          if (data.data.status === "success") {
            setFile(null);
            dispatch(
              appActions.showNotification({
                type: "success",
                message: "ファイルのアップロードが正常に完了しました",
              }),
            );
          } else onError();
        },
      }),
    );
  }, [menuId, file, selectedTemplate, currentSort, currentPagination]);

  const fetchFileUploadResult = useCallback(
    (sort: TableSortBy, pagination?: TablePagination) => {
      dispatch(appActions.showLoading());
      setCurrentSort(sort);
      setCurrentPagination(pagination ?? currentPagination);

      dispatch(
        fileActions.getFileUploadResults.started({
          mode: 0,
          menu_id: Number(menuId),
          sort_key: sort.field,
          sort_order: sort.order,
          page: pagination?.page ?? 1,
          size: pagination?.size ?? 100,
          onComplete: () => dispatch(appActions.hideLoading()),
        }),
      );
    },
    [menuId, currentPagination],
  );

  const handleOnCancel = useCallback(() => {
    setFile(null);
    setHasError(false);
  }, []);

  const handleOnSearch = useCallback(() => {
    navigateToPage(PageRoutes.FILE_UPLOAD_RESULT);
  }, []);

  const handleOnSelectTemplate = useCallback((name: string, id: number) => {
    setSelectedTemplate({ name, id });
  }, []);

  const checkFileExtension = useCallback(
    (file: File, type: "excel" | "csv") => {
      // check .csv .excel
      const ext = file.name.split(".").pop() ?? "";
      const size = getFileSizeInMB(file);
      const isExistExt =
        (type === "csv" && UPLOAD_FILE_TYPE.csv.includes(ext)) ||
        (type === "excel" && UPLOAD_FILE_TYPE.excel.includes(ext));
      const isExistSize = size < UPLOAD_FILE_SIZE_MB;
      if (isExistExt && isExistSize) {
        setFile(file);
      } else {
        let errorTxt = "";
        if (!isExistExt) {
          errorTxt += ErrorMessageManager.Instance.getErrorMessage(
            "file-not-support",
          ).replace("${type}", type === "excel" ? "Excel" : "CSV");
        }
        if (!isExistSize) {
          errorTxt !== "" && (errorTxt += "\n");
          errorTxt += ErrorMessageManager.Instance.getErrorMessage(
            "file-size-limit",
          ).replace("${size}", `${UPLOAD_FILE_SIZE_MB}MB`);
        }
        dispatch(
          appActions.showModal({
            title: errorTxt,
            buttons: [
              {
                text: "閉じる",
                variant: "filled",
              },
            ],
          }),
        );
      }
    },
    [dispatch, setFile],
  );

  const handleRowDownload = useCallback(
    (data: FileUploadResult, fileType: FileTypeNumber) => {
      dispatch(appActions.showLoading());
      dispatch(
        fileActions.downloadFileUploadResult.started({
          upload_result_id: data.id,
          file_type: fileType,
          onComplete: () => dispatch(appActions.hideLoading()),
          onSuccess: ({ data }) => window.open(data.download_url, "_blank"),
        }),
      );
    },
    [],
  );

  const handleRowDelete = useCallback((data: FileUploadResult) => {
    dispatch(
      appActions.showModal({
        title: "アップロード結果削除",
        buttons: [
          {
            text: "キャンセル",
            variant: "dark",
            onClick: () => dispatch(appActions.hideModal()),
          },
          {
            text: "削除",
            variant: "filled",
            onClick: () => {
              dispatch(appActions.hideModal());
              dispatch(appActions.showLoading());
              dispatch(
                fileActions.deleteFileUploadResults.started({
                  upload_result_id: [data.id],
                  onSuccess: () =>
                    fetchFileUploadResult({
                      field: "upload_datetime",
                      order: "desc",
                    }),
                  onComplete: () => dispatch(appActions.hideLoading()),
                }),
              );
            },
          },
        ],
      }),
    );
  }, []);

  const handleBulkDelete = useCallback(() => {
    const uploadResultIds = rowResults
      .filter((result) => result.selected)
      .map((result) => result.id);
    if (!uploadResultIds.length) return;

    dispatch(
      appActions.showModal({
        title: "アップロード結果削除",
        buttons: [
          {
            text: "キャンセル",
            variant: "dark",
            onClick: () => dispatch(appActions.hideModal()),
          },
          {
            text: "削除",
            variant: "filled",
            onClick: () => {
              dispatch(appActions.hideModal());
              dispatch(appActions.showLoading());
              dispatch(
                fileActions.deleteFileUploadResults.started({
                  upload_result_id: uploadResultIds,
                  onSuccess: () =>
                    fetchFileUploadResult(currentSort, currentPagination),
                  onComplete: () => dispatch(appActions.hideLoading()),
                }),
              );
            },
          },
        ],
      }),
    );
  }, [rowResults, currentSort, currentPagination]);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setRowResults(
        results.data.map((result) => ({
          ...result,
          selected: checked,
        })),
      );
    },
    [results],
  );

  const handleRowSelect = useCallback(
    (data: FileUploadResult, checked: boolean) => {
      setRowResults((prev) =>
        prev.map((row) => (row === data ? { ...row, selected: checked } : row)),
      );
    },
    [],
  );

  return {
    pageTitle,
    tableHeaders,
    templateOptions,
    fetchFileUploadResult,
    results: rowResults,
    total,
    file,
    setFile,
    setHasError,
    checkFileExtension,
    selectedTemplate,
    handleFileUpload,
    handleOnCancel,
    handleOnSearch,
    handleOnSelectTemplate,
    handleRowDownload,
    handleRowDelete,
    handleBulkDelete,
    handleSelectAll,
    handleRowSelect,
    isUploading,
    hasError,
  };
};
